import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useRef, useState } from "react";
import { SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from "@/lib/supabase";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { InvoiceTerm } from "@/components/offer/InvoiceTermsEditor";

interface BudgetLineAssignment {
  userId: string;
  hours: number;
}

/**
 * Payload til `create-project` edge function. Typen er bevisst åpen
 * (Record<string, unknown> for extra-feltene) fordi wizard-flyten har
 * bygd en stor "kitchen sink"-payload over flere VIB-issues, og typing
 * av hvert felt her gir liten verdi når edge function gjør sin egen
 * validering uansett. invoice_terms er typet eksplisitt fordi VIB-686
 * introduserer feltet og vi vil ha kompilator-støtte på det.
 */
interface CreateProjectPayload {
  project_code: string;
  name: string;
  customer_id: string | undefined;
  total_amount: number;
  estimated_deadline?: string;
  /** VIB-1297: valgfri forventet oppstartsdato. */
  estimated_start_date?: string;
  startup_invoice_pct: number;
  /**
   * VIB-686: Fleksible faktureringsvilkår. invoice_terms[0].pct skal
   * matche startup_invoice_pct for backward compat med lesere som ennå
   * ikke kjenner til invoice_terms.
   */
  invoice_terms: InvoiceTerm[];
  is_internal: boolean;
  project_status: string;
  project_type?: "fixed" | "hourly";
  production_lead_id?: string;
  payment_due_days: number;
  invoice_email?: string;
  // VIB-1191: kanoniske faktureringsref-felt (erstattet det engelske settet).
  deres_ref?: string;
  deres_ref_kode?: string;
  owners: Array<{ user_id: string; sales_unit_id?: string; ownership_pct: number }>;
  budget_lines: Array<{
    phase: string;
    description: string;
    quantity_hours: number;
    amount: number;
    estimated_cost?: number;
    cost_type: string;
    template_id?: string | null;
    /** VIB-995: tilvalg-markering (opsjon fra tilbudet). */
    is_optional?: boolean;
    /** VIB-1158: navn på tilvalgs-seksjonen (for per-option rubrikk). */
    option_label?: string | null;
    /** VIB-2866: rekkefølge innen fase fra tilbudet. */
    sort_order?: number | null;
    assignments?: BudgetLineAssignment[];
  }>;
  /**
   * Overhead/rabatt fra tilbudet ved konvertering. create-project skriver
   * dette til project_offer_metadata. Utelates for manuelt opprettede
   * prosjekter (feedback 25.06: overhead + rabatt kom ikke med).
   */
  offer_metadata?: {
    offer_total: number;
    overhead_pct: number;
    discount_pct: number;
    discount_amount: number;
    startup_invoice_pct: number;
    final_invoice_pct: number;
    include_travel_in_overhead?: boolean;
  };
}

interface UseCreateProjectOptions {
  /**
   * Når satt: kalles etter vellykket opprettelse i stedet for å navigere
   * automatisk til prosjektsiden. Brukes f.eks. av ConvertOfferDialog som
   * må gjøre ekstra post-create-arbeid (oppdatere offers.project_id,
   * lukke dialog) før navigasjon.
   */
  onSuccess?: (result: { project_id: string; project_code: string }) => Promise<void> | void;
  /** Hvis false hopper vi over auto-navigeringen selv om onSuccess ikke er satt. */
  navigateOnSuccess?: boolean;
}

interface CreateProjectResponse {
  po_sync_status?: "synced" | "partial" | "failed";
  po_sync_error?: string | null;
}

/**
 * VIB-2575: `po_sync_status: "partial"` ble tidligere ignorert, og PL fikk
 * «synkronisert til PowerOffice» selv når kunden eller prosjektet manglet i
 * PO. Da var oppstartsfakturaen blokkert uten at noen visste hvorfor. Feilen
 * er logget på prosjektet av create-project; her viser vi den.
 */
export function notifyCreated(isInternal: boolean, data: CreateProjectResponse): void {
  if (isInternal) {
    toast.success("Internprosjekt opprettet.");
    return;
  }
  if (data.po_sync_status && data.po_sync_status !== "synced") {
    toast.warning(
      "Prosjektet er opprettet, men ikke i PowerOffice. Det kan ikke faktureres før kunden er koblet.",
      {
        description: data.po_sync_error ?? undefined,
        duration: 15000,
      },
    );
    return;
  }
  toast.success("Prosjekt opprettet og synkronisert til PowerOffice. Du kan nå sende oppstartsfaktura.");
}

export function useCreateProject(options?: UseCreateProjectOptions) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  /** Synkron guard — loading-state oppdateres først neste render; dobbeltklikk
   *  før det kan ellers starte to create-project-kall. */
  const inFlightRef = useRef(false);
  const { activeUser } = useImpersonation();

  const createProject = async (payload: CreateProjectPayload) => {
    if (inFlightRef.current) return null;
    inFlightRef.current = true;
    setLoading(true);
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/create-project`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        // VIB-1184: aktør sendes i body (edge får ikke bruker-JWT).
        body: JSON.stringify({ ...payload, actor_user_id: activeUser?.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      notifyCreated(payload.is_internal, data);
      if (options?.onSuccess) {
        await options.onSuccess({ project_id: data.project_id, project_code: data.project_code });
      } else if (options?.navigateOnSuccess !== false) {
        navigate(`/prosjekter/${data.project_id}`);
      }
      return data as { project_id: string; project_code: string };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Feil: ${msg}`);
      throw err;
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  return { createProject, loading };
}
